import moment from 'moment';

/**
 * Date filter keys that should be formatted as MM/DD/YYYY.
 */
export const DATE_FILTER_KEYS = [
  'create_start_date',
  'create_end_date',
  'start_date',
  'end_date',
  'close_start_date',
  'close_end_date',
  'decision_start_date',
  'decision_end_date',
];

export const DYNAMIC_DATE_PRESETS_BACKWARD = [
  { id: 'fixed_30_days', label: 'Last 30 days' },
  { id: 'fixed_3_months', label: 'Last 3 months' },
  { id: 'fixed_6_months', label: 'Last 6 months' },
  { id: 'dynamic_custom', label: 'Custom Dynamic Date' },
];

export const DYNAMIC_DATE_PRESETS_FORWARD = [
  { id: 'fixed_30_days', label: 'Expired in 30 days' },
  { id: 'fixed_3_months', label: 'Expired in 3 months' },
  { id: 'fixed_6_months', label: 'Expired in 6 months' },
  { id: 'dynamic_custom', label: 'Custom Dynamic Date' },
];

export const ALL_DYNAMIC_PRESETS = [
  ...DYNAMIC_DATE_PRESETS_BACKWARD,
  ...DYNAMIC_DATE_PRESETS_FORWARD,
];

const DYNAMIC_FILTER_PATTERN = /^(?:fixed|custom)_(\d+)_(days|months)$/;

export function isDynamicFilter(value) {
  if (typeof value !== 'string') return false;
  return DYNAMIC_FILTER_PATTERN.test(value);
}

export function extractDynamicPreset(value) {
  if (!isDynamicFilter(value)) return null;

  if (value.startsWith('fixed_')) return value;
  if (value.startsWith('custom_')) return 'dynamic_custom';

  return null;
}

export function extractDynamicCustomConfig(value) {
  if (!isDynamicFilter(value)) return null;

  const match = value.match(/^custom_(\d+)_(days|months)$/);
  if (!match) return null;

  return {
    value: parseInt(match[1], 10),
    unit: match[2],
  };
}

export function getDynamicFilterLabel(value, presets = ALL_DYNAMIC_PRESETS) {
  if (!isDynamicFilter(value)) return value;

  const fixedPreset = presets.find(p => p.id === value);
  if (fixedPreset) return fixedPreset.label;

  const customConfig = extractDynamicCustomConfig(value);
  if (customConfig) {
    const isForward = presets === DYNAMIC_DATE_PRESETS_FORWARD;
    const prefix = isForward ? 'Expired in' : 'Last';
    const unitLabel = customConfig.unit === 'months' ? 'months' : 'days';
    return `${prefix} ${customConfig.value} ${unitLabel}`;
  }

  const dynamicConfig = value.match(DYNAMIC_FILTER_PATTERN);
  if (dynamicConfig) {
    const isForward = presets === DYNAMIC_DATE_PRESETS_FORWARD;
    const prefix = isForward ? 'Expired in' : 'Last';
    return `${prefix} ${dynamicConfig[1]} ${dynamicConfig[2]}`;
  }

  const preset = extractDynamicPreset(value);
  const presetObj = presets.find(p => p.id === preset);
  return presetObj ? presetObj.label : value;
}

export function formatFilterDateValue(key, value) {
  if (isDynamicFilter(value)) {
    const dynamicPresets = key?.toString().toLowerCase().includes('expire')
      ? DYNAMIC_DATE_PRESETS_FORWARD
      : ALL_DYNAMIC_PRESETS;
    return getDynamicFilterLabel(value, dynamicPresets);
  }

  //check is the key includes or noe
  if (DATE_FILTER_KEYS.includes(key)) {
    return moment(value).format('MM/DD/YYYY');
  }

  if (key === 'filed_date' && value.includes(' to ')) {
    const [start, end] = value.split(' to ');
    return `${moment(start).format('MM/DD/YYYY')} to ${moment(end).format('MM/DD/YYYY')}`;
  }

  return value;
}

function isForwardDynamicDateFilter(filter) {
  if (filter.dynamic_date_direction === 'forward') return true;
  if (filter.dynamic_date_direction === 'backward') return false;
  return (filter.name || '').toLowerCase().includes('expire');
}

export function resolveDynamicDateToAbsolute(value, filter = {}) {
  if (!value) return { startDate: '', endDate: '' };

  const todayStart = moment().startOf('day');
  const todayEnd = moment().endOf('day');

  const match = value.match(DYNAMIC_FILTER_PATTERN);
  if (!match) return { startDate: '', endDate: '' };

  const dynamicRangeLength = parseInt(match[1], 10);
  const unit = match[2];
  if (!Number.isFinite(dynamicRangeLength) || dynamicRangeLength <= 0) return { startDate: '', endDate: '' };

  const isForward = isForwardDynamicDateFilter(filter);
  if (isForward) {
    return {
      startDate: todayStart.format('YYYY-MM-DD'),
      endDate: moment(todayEnd).add(dynamicRangeLength, unit).format('YYYY-MM-DD'),
    };
  }
  return {
    startDate: moment(todayStart).subtract(dynamicRangeLength, unit).format('YYYY-MM-DD'),
    endDate: todayEnd.format('YYYY-MM-DD'),
  };
}

export function resolveDynamicFiltersToAbsolute(filters = {}, filterOptions = []) {
  const resolvedFilters = { ...(filters || {}) };
  const dynamicFilters = resolvedFilters.dynamic_filters;
  delete resolvedFilters.dynamic_filters;

  if (!Array.isArray(dynamicFilters)) return resolvedFilters;

  dynamicFilters.forEach(item => {
    if (!item?.key || !item?.value) return;

    const filter = (filterOptions || []).find(option => option.name === item.key);
    if (filter && filter.data_type !== 'date') return;

    const { startDate, endDate } = resolveDynamicDateToAbsolute(item.value, filter || item);

    const startKey = item.start_field || filter?.keys?.[0];
    const endKey = item.end_field || filter?.keys?.[1];

    if (startKey && startDate) {
      resolvedFilters[startKey] = startDate;
    }
    if (endKey && endDate) {
      resolvedFilters[endKey] = endDate;
    }
  });

  return resolvedFilters;
}
